import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractDynamicComponent } from '@components/abstracts';
import { PopinRealisationsClientsComponent } from '@components/popin-realisations-clients';
import { PushRealisationsClients } from '@components/push-realisations-clients/models';
import { ButtonComponent } from '@shared/components/button';
import { ButtonMiddleOfficeComponent } from '@shared/components/button-middle-office';
import { ImageComponent } from '@shared/components/image';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { Project } from '@shared/models';
import { DialogService } from '@shared/services/dialog';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { LogsService } from '@shared/services/logs';
import { MiddleOfficeService } from '@shared/services/middle-office';
import { PageStore } from '@stores';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-push-realisations-client',
  imports: [
    TitleComponent,
    ImageComponent,
    ButtonComponent,
    SvgComponent,
    NgClass,
    NgTemplateOutlet,
    ButtonMiddleOfficeComponent,
  ],
  templateUrl: './push-realisations-clients.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PushRealisationsClientsComponent extends AbstractDynamicComponent<PushRealisationsClients> {
  readonly isMiddleOfficeAvailable = inject(MiddleOfficeService).isMiddleOfficeAvailable;
  readonly slidesElementRef = viewChild.required<ElementRef<HTMLElement>>('slidesElementRef');
  readonly projects = signal<Project[]>([]);
  readonly nSlides = computed(() => Math.ceil(this.projects().length / 2));
  currentSliderIndex = signal(0);
  private readonly dialogService = inject(DialogService);
  private readonly storeUuid = inject(PageStore).selectPath((state) => state.config.context?.uuid);
  private readonly elasticSearchService = inject(ElasticSearchService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logs = inject(LogsService);

  constructor() {
    super();
    effect(() => {
      const storeUuid = this.storeUuid();
      if (!storeUuid) return;
      this.elasticSearchService
        .getShowcases({
          context: 'store',
          storeUuid,
          size: 10,
          from: 0,
        })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((res) => {
            this.projects.set(res.showcases);
          })
        )
        .subscribe({
          error: (err) => this.logs.error('PushRealisationsClientsComponent', 'getShowcases failed', err),
        });
    });
  }

  moveSlider(direction: 'prev' | 'next'): void {
    const nSlides = this.nSlides();

    if (nSlides < 1) return;

    const slideWidth = this.slidesElementRef().nativeElement.clientWidth;
    let newIndex = this.currentSliderIndex();

    if (direction === 'next') {
      newIndex = (newIndex + 1) % nSlides;
    } else {
      newIndex = (newIndex - 1 + nSlides) % nSlides;
    }

    this.currentSliderIndex.set(newIndex);
    this.slidesElementRef().nativeElement.scrollTo({
      left: newIndex * (slideWidth + 8),
      behavior: 'smooth',
    });
  }

  openRealisationsClientsPopup(selectedProjectIndex: number) {
    const projects = this.projects();
    this.dialogService.openDialog(PopinRealisationsClientsComponent, 'BIG', {
      data: {
        selectedProjectIndex,
        projects,
      },
    });
  }
}
